import { titleCase, spacer, EXCLUDED_KEYS, SECONDARY_KEYS, formatAmount } from './ksExporterUtils';
import { ExportData, ExportDataRow, ExportFilters } from './types';

interface ProcessPropertiesProps {
  exportDataRow: ExportDataRow;
  key: string;
  secondaryKey?: string;
  label?: string;
  firstColumn: boolean;
  allowMoney: boolean;
  allowPercentage: boolean;
}

const formatCSVMoney = (value: number | string) => {
  return value && value !== 0 && value !== ' ' ? formatAmount(value as number, 2, true) : ' ';
};

const formatCSVPercentage = (value: number | string) => {
  return value && value !== 0 && value !== ' ' ? formatAmount((value as number) * 100, 2, true, true) + '%' : ' ';
};

// This function extract the properties Money and Percentages from the ExportData object and returns a CSV object with the properly formatted values and a flattened headers array to set the order of the columns in the CSV file.
const processProperties = ({
  exportDataRow,
  key,
  secondaryKey,
  label,
  firstColumn,
  allowMoney,
  allowPercentage,
}: ProcessPropertiesProps) => {
  const csvObj: Record<string, any> = {};
  const flattenedHeaders: { label: string; key: string }[] = [];
  const parentObj = exportDataRow[key];
  if (secondaryKey && !(secondaryKey in parentObj)) return null;

  const pointerObj = secondaryKey ? parentObj[secondaryKey] : parentObj;
  const localLabel = secondaryKey ? ' ' + (label ? label : '') : '';

  if (!pointerObj) {
    return null;
  }
  if (allowMoney && 'money' in pointerObj) {
    csvObj[`${key}${localLabel} $`] = formatCSVMoney(pointerObj.money);
    if (firstColumn) flattenedHeaders.push({ label: `${key}${localLabel} $`, key: `${key}${localLabel} $` });
  }
  if (allowPercentage && 'percent' in pointerObj) {
    csvObj[`${key}${localLabel} %`] = formatCSVPercentage(pointerObj.percent);
    if (firstColumn) flattenedHeaders.push({ label: `${key}${localLabel} %`, key: `${key}${localLabel} %` });
  }
  return { csvObj, flattenedHeaders };
};

const shouldIncludeSecondaryKey = (secondaryKey: (typeof SECONDARY_KEYS)[number], filters?: ExportFilters) => {
  if (!filters) return true;
  if (secondaryKey === 'actuals') return filters.actuals !== false;
  if (secondaryKey === 'budget') return filters.budget === true;
  if (secondaryKey === 'variance') return filters.variance === true;
  return true;
};

// This funtion processes the exportObject to create the CSV output
export const processCSV = (exportObject: ExportData, filters?: ExportFilters) => {
  // Setup the Headers for the CSV to be able to order them properly
  const flattenedHeaders: { label: string; key: string }[] = [];
  const allowMoney = filters?.money !== false;
  const allowPercentage = filters?.percentage !== false;
  // Flatten data for CSV export and maintain column order
  const flattenedData = exportObject.data.map((row: ExportDataRow, index: number) => {
    const csvObj: Record<string, string | number> = {};
    const firstColumn = index === 0;
    // Set properties in desired order
    csvObj.GL = row.GL;
    // We indent the accounts column to match the report using spacer
    csvObj.Account = spacer(row.style.level) + row.accounts;
    if (firstColumn) flattenedHeaders.push({ label: 'GL #', key: 'GL' });
    if (firstColumn) flattenedHeaders.push({ label: 'Accounts', key: 'Account' });

    // If we are not exporting percentages, we can skip this step
    if (row.percentOf) {
      csvObj['% of'] = row.percentOf;
      if (firstColumn) flattenedHeaders.push({ label: '% of', key: '% of' });
    }

    // check every property in the row
    Object.keys(row).forEach(key => {
      if (EXCLUDED_KEYS.includes(key as any)) return;

      SECONDARY_KEYS.filter(sk => shouldIncludeSecondaryKey(sk, filters)).forEach(currentKey => {
        const result = processProperties({
          exportDataRow: row,
          key,
          secondaryKey: currentKey,
          firstColumn,
          label: titleCase(currentKey),
          allowMoney,
          allowPercentage,
        });
        if (result) {
          Object.assign(csvObj, result.csvObj);
          flattenedHeaders.push(...result.flattenedHeaders);
        }
      });
    });
    return csvObj;
  });
  return { data: flattenedData, headers: flattenedHeaders };
};
